import React from 'react';
import { Stack } from '@fiverr-private/layout_components';
import { Link, Text } from '@fiverr-private/typography';
import { I18n, translate } from '@fiverr-private/i18n-react';
import { currencyFormat } from '@fiverr-private/localization';
import { Box } from '@fiverr-private/theme';
import { useSellerConfigData } from '../../api/useSellerConfigData';
import { useOrganizationPackagesData } from '../../api/useOrganizationPackagesData';
import BoldBodyInfoCard from './BoldBodyInfoCard';
import { ComplianceType } from './types';
import { getInfoGridPaymentData } from './helpers';

interface InfoGridProps {
    price: number;
    requeseteeUserName: string;
    complianceType: ComplianceType;
    requeseteeId: number;
    onUpgradeClick: () => void;
}

interface ServiceCostProps {
    beforeDiscount: number;
    afterDiscount: number;
    currency: string;
    onUpgradeClick: () => void;
}

const InfoGrid = ({ requeseteeId, price, requeseteeUserName, complianceType, onUpgradeClick }: InfoGridProps) => {
    const { workforceClassificationPackageData, backgroundCheckPackageData } = useSellerConfigData(requeseteeId);
    const { sellerEntityType } = useSellerConfigData(requeseteeId);
    const { timeEstimation, discountInPercents, monthlyCredit, currency } = getInfoGridPaymentData(
        complianceType,
        backgroundCheckPackageData,
        workforceClassificationPackageData
    );

    const _monthlyCredit = monthlyCredit?.value === 0 || monthlyCredit?.value ? String(monthlyCredit?.value) : '';

    const copyPrefix = 'compliance.info_grid';

    const freelancerTitle = translate(`${copyPrefix}.${sellerEntityType}`);
    const timeEstimationTitle = translate(`${copyPrefix}.time_estimate`);
    const monthlyCreditTitle = translate(`${copyPrefix}.monthly_credit`);
    const checks = translate(`${copyPrefix}.${complianceType}.body`);
    const monthlyCreditBody = _monthlyCredit !== '' ? `${_monthlyCredit} ${checks}` : '';
    const discountPrice = monthlyCredit && discountInPercents ? price - (discountInPercents * price) / 100 : price;
    return (
        <Stack
            direction="row"
            gap={{ default: '6', sm: '10' }}
            wrap={{ default: 'wrap', sm: 'nowrap' }}
            justifyContent="stretch"
        >
            <Stack gap="6" minWidth={'170px'} width="100%" justifyContent="flexStart">
                <BoldBodyInfoCard title={freelancerTitle} body={requeseteeUserName} complianceType={complianceType} />
                <BoldBodyInfoCard title={timeEstimationTitle} body={timeEstimation} complianceType={complianceType} />
            </Stack>
            <Stack gap="6" width="100%" minWidth={'170px'} justifyContent="flexStart">
                {monthlyCreditBody && (
                    <BoldBodyInfoCard
                        title={monthlyCreditTitle}
                        body={monthlyCreditBody}
                        monthlyCredit={_monthlyCredit}
                        info={true}
                        complianceType={complianceType}
                    />
                )}
                <ServiceCost
                    beforeDiscount={price}
                    afterDiscount={discountPrice}
                    currency={currency}
                    onUpgradeClick={onUpgradeClick}
                />
            </Stack>
        </Stack>
    );
};

const ServiceCost: React.FC<ServiceCostProps> = ({ beforeDiscount, afterDiscount, currency, onUpgradeClick }) => {
    const isWithDiscount = beforeDiscount !== afterDiscount;
    const { canUpgradeToPro } = useOrganizationPackagesData();
    const formatBeforeDiscount = currencyFormat({
        value: beforeDiscount,
        currencyCode: currency,
    });
    const formatAfterDiscount = currencyFormat({
        value: afterDiscount,
        currencyCode: currency,
    });
    const priceBeforeDiscount = (
        <Text fontWeight={'semibold'} size={'b_md'}>
            {formatAfterDiscount}
        </Text>
    );
    return (
        <Stack gap={'2'} direction={'column'} justifyContent={'spaceBetween'}>
            <Text color={'bodySecondary'} size={'b_xs'}>
                <I18n k={'compliance.info_grid.price'} />
            </Text>
            <Stack gap={'2'}>
                {isWithDiscount ? (
                    <>
                        <Box color={'grey_600'}>
                            <Text color={'inherit'} size={'b_md'} decoration="lineThrough">
                                {formatBeforeDiscount}
                            </Text>
                        </Box>
                        {priceBeforeDiscount}
                    </>
                ) : (
                    priceBeforeDiscount
                )}

                {canUpgradeToPro && (
                    <Link cursor="pointer" size="b_sm" onClick={onUpgradeClick}>
                        <I18n k="compliance.upsale.upgrade_link" />
                    </Link>
                )}
            </Stack>
        </Stack>
    );
};
export default InfoGrid;
